/* eslint-disable no-mixed-spaces-and-tabs */
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import EditOffOutlinedIcon from "@mui/icons-material/EditOffOutlined";
import HistoryIcon from "@mui/icons-material/History";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Box, Divider, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { useCallback, useMemo, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  getRepLevel,
  getRepType,
  hasPermission,
} from "../../../../../utils/helpers";
import {
  addSkillInterested,
  clearSkillAuditLog,
  fetchWorkerSkillAuditLog,
  setIsSkillDetailsDialogOpen,
} from "../../../../workers/store/manage-workers-slice";
import { Skill, SkillLevel } from "../../../../workers/types";
import { MIN_CROSS_SKILL_ROW_HEIGHT } from "../../../constants";
import ApproveSkillUpgradeDialog from "./approve-skill-upgrade-dialog";
import SkillRepresentationCell from "./column-skill-representations-cell";

interface Params {
  row: {
    skills: Skill[];
    skillInterests?: Skill[];
    [key: string]: any;
  };
}

interface ColumnSkillCellProps {
  params: Params;
  skill: Skill;
  handleSkillLevels: (
    params: Params,
    skill: Skill,
    level: SkillLevel,
    setState: React.Dispatch<React.SetStateAction<Skill | null>>,
    state: Skill | null
  ) => void;
  allSkillLevels: SkillLevel[];
  handleAddSkillInterested: (
    params: Params,
    skill: Skill,
    setState: React.Dispatch<React.SetStateAction<Skill | null>>
  ) => void;
  skillsUpdatedListInSession: Skill[];
  notAssignedSkillLevel?: SkillLevel;
  onUpdateSkillLevel?: (...args: any[]) => void;
}

const ColumnSkillCell: React.FC<ColumnSkillCellProps> = ({
  params,
  skill,
  handleSkillLevels,
  allSkillLevels,
  notAssignedSkillLevel,
  handleAddSkillInterested,
  skillsUpdatedListInSession,
  onUpdateSkillLevel,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const skillId = skill.skillId ?? skill.id;
  const workerSkill = params.row.skills.find(
    (x) => parseInt(String(x.skillId)) === parseInt(String(skillId))
  );

  const isInterested = params.row.skillInterests
    ? params.row.skillInterests.find(
      (x: any) => parseInt(String(x.skillId)) === parseInt(String(skillId))
    )
    : false;

  const isUpgradeInterest = isInterested
    && isInterested.skillLevelId
    && parseInt(String(isInterested.skillLevelId)) > 0;

  const targetLevel = isUpgradeInterest
    ? allSkillLevels.find((level: any) => level.id === parseInt(isInterested.skillLevelId))
    : null;

  const [state, setState] = useState<Skill | null>(workerSkill || null);
  const [interestState, setInterestState] = useState<any>(isInterested || null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [clickedLevelId, setClickedLevelId] = useState<number | null>(null);
  const [workerSkillsAnchorEl, setWorkerSkillsAnchorEl] =
    useState<null | HTMLElement>(null);
  const [otherSkillsAnchorEl, setOtherSkillsAnchorEl] =
    useState<null | HTMLElement>(null);
  const openWorkerSkills = Boolean(workerSkillsAnchorEl);
  const openOtherSkills = Boolean(otherSkillsAnchorEl);

  const userRolesWithPermission = useSelector(
    (state: any) => state.user.userData.userRolesWithPermissions
  );
  const { userRoles, userWorkCenters } = useSelector(
    (state: any) => ({
      userRoles: state.user.userData.userRoles,
      userWorkCenters:
        state.manageWorkers.skillMatrixAppliedFilters.workcenters?.split(",") ||
        [],
    })
  );


  const isReadOnlyUser = useMemo(() => {
    return userRoles.includes("ReadOnly");
  }, [userRoles]);

  const isRestrictedRole = useMemo(() => {
    return (
      !userRoles.includes("GATAdmin") && !userRoles.includes("HR Manager")
    );
  }, [userRoles]);

  const hasUpdatePermission = useMemo(() => {
    return hasPermission(userRolesWithPermission, "CROSS_SKILLING.UPDATE_SKILL_LEVEL");
  }, [userRolesWithPermission]);

  const isInteractionDisabled = useMemo(() => {
    return isReadOnlyUser;
  }, [isReadOnlyUser]);

  const canEdit = useMemo(() => {
    return !isReadOnlyUser;
  }, [isReadOnlyUser]);

  const isInUserWorkcenterAndHasPermission = useMemo(() => {
    const workerWorkCenters = params.row.workCenterIds?.split(",") || [];
    return workerWorkCenters.some((wc: string) => userWorkCenters.includes(wc));
  }, [params.row.workCenterIds, userWorkCenters]);

  const handleisInUserWorkcenterAlert = useCallback(() => {
    confirm({
      title: "",
      description: t("cross-skilling_no-workcenter-access"),
      confirmationText: t("common_close"),
      cancellationText: "",
    });
  }, [confirm, t]);

  const handleWorkerSkillsClick = (event: React.MouseEvent<HTMLElement>) => {
    if (
      hasPermission(
        userRolesWithPermission,
        "CROSS_SKILLING.UPDATE_SKILL_LEVEL"
      )
    ) {
      setWorkerSkillsAnchorEl(event.currentTarget);
    }
  };

  const handleWorkerSkillsClose = () => {
    setWorkerSkillsAnchorEl(null);
  };

  const updateSkillLevel = useCallback(
    (
      params: Params,
      skill: Skill,
      level: SkillLevel,
      state: Skill | null,
      handleClose: () => void
    ) => {
      handleSkillLevels(params, skill, level, setState, state);
      handleClose();
    },
    [handleSkillLevels]
  );

  const handleViewDetails = useCallback(() => {
    dispatch(fetchWorkerSkillAuditLog({
      workerId: params.row.id,
      skillId: skillId,
      workerData: params.row,
      skillData: skill,
    }));
    dispatch(setIsSkillDetailsDialogOpen(true));
    handleWorkerSkillsClose();
  }, [dispatch, params.row, skillId, skill]);

  const hasAcceptInterestPermission = useMemo(() => {
    return hasPermission(userRolesWithPermission, "CROSS_SKILLING.ACCEPT_INTEREST");
  }, [userRolesWithPermission]);

  const handleApproveUpgrade = useCallback((levelId: number) => {
    if (isInterested) {
      setClickedLevelId(levelId);
      setApproveDialogOpen(true);
    } else {
      handleWorkerSkillsClose();
    }
  }, [isInterested]);

  const handleApproveConfirm = useCallback((selectedLevelId: number, comment: string) => {
    if (!isInterested) return;
    const selectedLevel = allSkillLevels.find((l: any) => l.id === selectedLevelId);
    if (!selectedLevel) return;
    dispatch(addSkillInterested({ id: isInterested.id, overrideLevelId: selectedLevelId, comment }));
    setState((prev: any) => ({
      ...prev,
      level: selectedLevel.level,
      levelId: selectedLevel.id,
      repCode: selectedLevel.repCode,
      backColorCode: selectedLevel.backColorCode,
      foreColorCode: selectedLevel.foreColorCode,
      value: selectedLevel.value,
    }));
    setInterestState(null);
    if (onUpdateSkillLevel) {
      onUpdateSkillLevel(
        state,
        {
          skillId: skillId,
          levelId: selectedLevel.id,
          presentLevelId: -1,
          workerId: params.row.id,
        },
        { ...selectedLevel, levelId: selectedLevel.id }
      );
    }
    setApproveDialogOpen(false);
    handleWorkerSkillsClose();
  }, [isInterested, allSkillLevels, dispatch, onUpdateSkillLevel, state, skillId, params.row.id]);

  const existingSkillUpdated = skillsUpdatedListInSession.find(
    (x) => x.workerId === params.row.id && x.skillId === skill.skillId
  );

  // When this skill was already updated in this session (e.g. approved in another job-role column),
  // suppress the stale upgrade-request indicators so the popup shows clean state.
  const effectiveInterestState = existingSkillUpdated ? null : interestState;
  const effectiveIsUpgradeInterest = existingSkillUpdated ? false : isUpgradeInterest;
  const effectiveTargetLevel = existingSkillUpdated ? null : targetLevel;
  const effectiveIsInterested = existingSkillUpdated ? null : isInterested;

  const skillLevelMenu = useMemo(
    () => (
      <>
        <Menu
          id="worker-skills-menu"
          anchorEl={workerSkillsAnchorEl}
          open={openWorkerSkills}
          onClose={handleWorkerSkillsClose}
          slotProps={{
            paper: {
              "aria-labelledby": "worker-skills-button",
              sx: {
                minWidth: "200px",
              },
            },
          }}
        >
          {allSkillLevels.map((level, index) => {
            const isTargetLevel = !!(effectiveInterestState && effectiveIsUpgradeInterest && effectiveTargetLevel && level.id === effectiveTargetLevel.id);
            return (
              <Box key={level.id}>
                <MenuItem
                  value={level.repCode}
                  onClick={() => {
                    if (effectiveInterestState && effectiveIsUpgradeInterest && hasAcceptInterestPermission) {
                      handleApproveUpgrade(level.id);
                    } else {
                      updateSkillLevel(params, skill, level, state, handleWorkerSkillsClose);
                    }
                  }}
                  sx={isTargetLevel ? { backgroundColor: "rgba(255, 152, 0, 0.08)" } : {}}
                >
                  <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" width="100%">
                    <Box display="flex" flexDirection="row" alignItems="center">
                      <SkillRepresentationCell
                        level={level.level}
                        value={level.value}
                        backgroundColor={level.backColorCode}
                        foregroundColor={level.foreColorCode}
                        representationType={getRepType(level.repCode)}
                        representationLevel={getRepLevel(level.repCode, level.level)}
                        border={1}
                        backgroundWidth="35px"
                        backgroundHeight="35px"
                      />
                      <Typography ml={1}>{level.name}</Typography>
                    </Box>
                    {isTargetLevel && (
                      <Tooltip title={t("cross-skilling_worker-requested-upgrade-level")} arrow>
                        <Box
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            backgroundColor: "#FF9800",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            ml: 1,
                            flexShrink: 0,
                          }}
                        >
                          <ArrowUpwardIcon sx={{ fontSize: 12, color: "white" }} />
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </MenuItem>
                {index === allSkillLevels.length - 1 && <Divider />}
              </Box>
            );
          })}
          <MenuItem key="view-details" onClick={handleViewDetails}>
            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              <Box sx={{ width: 35, height: 35, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <HistoryIcon sx={{ fontSize: 24, color: "primary.main" }} />
              </Box>
              <Typography sx={{
                fontFamily: "Roboto",
                fontSize: "16px",
                color: "primary.main",
              }}>{t("cross-skilling_view-log")}</Typography>
            </Box>
          </MenuItem>
        </Menu>
        {effectiveIsInterested && effectiveTargetLevel && (
          <ApproveSkillUpgradeDialog
            open={approveDialogOpen}
            onClose={() => { setApproveDialogOpen(false); handleWorkerSkillsClose(); }}
            onConfirm={handleApproveConfirm}
            workerFirstName={params.row.firstName}
            skillName={skill.name}
            requestedLevel={effectiveTargetLevel}
            defaultSelectedLevelId={clickedLevelId ?? effectiveTargetLevel.id}
            allSkillLevels={allSkillLevels}
          />
        )}
      </>
    ),
    [
      workerSkillsAnchorEl,
      openWorkerSkills,
      allSkillLevels,
      updateSkillLevel,
      params,
      skill,
      state,
      handleViewDetails,
      effectiveInterestState,
      effectiveIsUpgradeInterest,
      effectiveTargetLevel,
      effectiveIsInterested,
      hasAcceptInterestPermission,
      handleApproveUpgrade,
      approveDialogOpen,
      handleApproveConfirm,
      clickedLevelId,
      t,
    ]
  );

  if (!state && interestState && !existingSkillUpdated) {
    return (
      <>
        <Box
          sx={{
            borderRadius: "3px",
            height: MIN_CROSS_SKILL_ROW_HEIGHT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: !isInteractionDisabled ? "pointer" : "default",
            width: "100%",
          }}
          key={`${skill.skillId}-interested`}
          onClick={(event) => {
            if (!isInteractionDisabled) {
              if (isInUserWorkcenterAndHasPermission) {
                if (isUpgradeInterest && hasAcceptInterestPermission && targetLevel) {
                  // Upgrade interest: always use the new approval dialog (level override + comment)
                  handleApproveUpgrade(targetLevel.id);
                } else if (isEvaluationBlocked) {
                  handleWorkerSkillsClick(event);
                } else {
                  // General interest (no specific upgrade level): direct update
                  handleAddSkillInterested(params, skill, (updater) => {
                    setState(updater);
                    setInterestState(null);
                  });
                }
              } else {
                handleisInUserWorkcenterAlert();
              }
            }
          }}
        >
          <Typography
            textAlign="center"
            sx={{ fontSize: "12px", color: "#49454F" }}
          >
            {t("cross-skilling_interested")}
          </Typography>
        </Box>
        {skillLevelMenu}
      </>
    );
  }

  //when the skill level is changed for one job, update other jobs with the same skill level
  if (existingSkillUpdated) {
    return (
      <>
        <Box
          aria-controls={openWorkerSkills ? "worker-skills-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={openWorkerSkills ? "true" : undefined}
          onClick={(event) => {
            if (!isInteractionDisabled) {
              if (isInUserWorkcenterAndHasPermission) {
                handleWorkerSkillsClick(event);
              } else {
                handleisInUserWorkcenterAlert();
              }
            }
          }}
          sx={{
            cursor: isInteractionDisabled
              ? "default"
              : isInUserWorkcenterAndHasPermission
                ? "pointer"
                : "default",
            width: "100%",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <SkillRepresentationCell
              representationType={getRepType(existingSkillUpdated.repCode)}
              representationLevel={getRepLevel(
                existingSkillUpdated.repCode,
                existingSkillUpdated.level
              )}
              value={existingSkillUpdated.value}
              level={existingSkillUpdated.level}
              backgroundColor={existingSkillUpdated.backColorCode}
              foregroundColor={existingSkillUpdated.foreColorCode}
              height={20}
              width={20}
              backgroundWidth={"100%"}
              backgroundHeight={"100%"}
            />
          </Box>
        </Box>
        {skillLevelMenu}
      </>
    );
  }

  if (!state) {
    if (notAssignedSkillLevel) {
      return (
        <>
          <Box
            aria-controls={openOtherSkills ? "other-skills-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={openOtherSkills ? "true" : undefined}
            onClick={(event: any) => {
              if (!isInteractionDisabled) {
                if (isInUserWorkcenterAndHasPermission) {
                  handleWorkerSkillsClick(event);
                } else {
                  handleisInUserWorkcenterAlert();
                }
              }
            }}
            sx={{
              cursor:
                !isInteractionDisabled || isInUserWorkcenterAndHasPermission
                  ? "pointer"
                  : "default",
              width: "100%",
              height: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <SkillRepresentationCell
                representationType={getRepType(notAssignedSkillLevel.repCode)}
                representationLevel={getRepLevel(
                  notAssignedSkillLevel.repCode,
                  0
                )}
                value={notAssignedSkillLevel.value}
                height={20}
                width={20}
                backgroundWidth={"100%"}
                backgroundHeight={"100%"}
              />
            </Box>
          </Box>
          {skillLevelMenu}
        </>
      );
    } else {
      return (
        <>
          <Box
            aria-controls={openOtherSkills ? "other-skills-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={openOtherSkills ? "true" : undefined}
            onClick={(event: any) => {
              if (!isInteractionDisabled) {
                if (isInUserWorkcenterAndHasPermission) {
                  handleWorkerSkillsClick(event);
                } else {
                  handleisInUserWorkcenterAlert();
                }
              }
            }}
            sx={{
              cursor:
                !isInteractionDisabled || isInUserWorkcenterAndHasPermission
                  ? "pointer"
                  : "default",
              width: "100%",
              height: "100%",
            }}
          >
            <Box
              sx={{
                height: MIN_CROSS_SKILL_ROW_HEIGHT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: "14px",
                  textTransform: "uppercase",
                }}
              >
                --
              </Typography>
            </Box>
          </Box>
          {skillLevelMenu}
        </>
      );
    }
  } else {
    return (
      <>
        <Box
          aria-controls={openWorkerSkills ? "worker-skills-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={openWorkerSkills ? "true" : undefined}
          onClick={(event: any) => {
            if (!isInteractionDisabled) {
              if (isInUserWorkcenterAndHasPermission) {
                handleWorkerSkillsClick(event);
              } else {
                handleisInUserWorkcenterAlert();
              }
            }
          }}
          sx={{
            cursor:
              !isInteractionDisabled || isInUserWorkcenterAndHasPermission
                ? "pointer"
                : "default",
            width: "100%",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              position: "relative",
            }}
          >
            <SkillRepresentationCell
              representationType={getRepType(state.repCode)}
              representationLevel={getRepLevel(state.repCode, state.level)}
              value={state.value}
              level={state.level}
              backgroundColor={state.backColorCode}
              foregroundColor={state.foreColorCode}
              height={20}
              width={20}
              backgroundWidth="100%"
              backgroundHeight="100%"
            />
            {interestState && isUpgradeInterest && (
              <Tooltip title={targetLevel ? t("cross-skilling_upgrade-requested-to", { level: targetLevel.name }) : t("cross-skilling_upgrade-requested")} arrow>
                <Box
                  sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: "#FF9800",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ArrowUpwardIcon sx={{ fontSize: 11, color: "white" }} />
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>
        {skillLevelMenu}
      </>
    );
  }
};

export default ColumnSkillCell;
